import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { Switch, FormControlLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import NewInput from '../../Components/NewInput/NewInput';
import ApiSelect from '../../Components/ApiSelect/ApiSelect';
import noImage from '../../Images/photo-camera.png';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import { productAPI } from '../../services/apiService';
import './pForm.css';

const convertPersianToEnglishDigits = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
};

const convertEnglishToPersianDigits = (str) => {
    if (!str) return '';
    return str.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
};

const filterNumericInput = (str) => str.replace(/[^0-9۰-۹]/g, '');

function ProductForm() {
    const { t } = useTranslation();
    const token = localStorage.getItem('token');
    const group = localStorage.getItem('userGroup');
    const navigate = useNavigate();

    const [form, setForm] = useState({
        title: '',
        description: '',
        price: '',
        inStock: true,
        image: '',
        category: '',
        priority: 100,
    });

    const [preview, setPreview] = useState(noImage);

    const handleNumericKeyDown = (e) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

        if (!allowedKeys.includes(e.key) && !e.key.match(/[0-9۰-۹]/)) {
            e.preventDefault();
        }
    };

    const handleNumericChange = (e) => {
        const { name, value } = e.target;

        const filteredValue = filterNumericInput(value);

        setForm((prev) => ({
            ...prev,
            [name]: filteredValue,
        }));
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({
                ...prev,
                image: file,
            }));
            const reader = new FileReader();
            reader.onloadend = () => setPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleCategorySelect = (selectedOption) => {
        setForm((prev) => ({
            ...prev,
            category: selectedOption,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.title || !form.price || !form.category) {
            alert(t('product_required_fields'));
            return;
        }

        const formData = new FormData();
        formData.append('title', form.title);
        formData.append('description', form.description);
        formData.append('price', Number(convertPersianToEnglishDigits(form.price)));
        formData.append('available', form.inStock);
        formData.append('priority', Number(convertPersianToEnglishDigits(form.priority)));
        formData.append('item_type', form.category?.value || '');
        if (form.image) formData.append('image', form.image);

        try {
            await productAPI.create(formData);
            navigate('/products', { state: { successMessage: t('add_product_success') } });
        } catch (error) {
            console.error(error);
            alert(t('submit_error'));
        }
    };

    return (
        <div className="add_new">
            <div className="home_sidebar">
                <Sidebar />
            </div>
            <div className="new_page">
                <Navbar />
                <div className="new_page_main">
                    <form className="productForm" onSubmit={handleSubmit}>
                        <NewInput
                            id="name"
                            label={t('product_name')}
                            name="title"
                            type="text"
                            placeholder={t('enter_product_name')}
                            value={form.title}
                            onChange={handleInputChange}
                            className="pName"
                            required
                        />

                        <NewInput
                            id="price"
                            label={t('price_toman')}
                            name="price"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9۰-۹]*"
                            placeholder={t('enter_product_price')}
                            value={form.price}
                            onChange={handleNumericChange}
                            onKeyDown={handleNumericKeyDown}
                            className="pPrice"
                            required
                        />

                        <NewInput
                            id="description"
                            label={t('description')}
                            name="description"
                            type="text"
                            placeholder={t('enter_product_description')}
                            value={form.description}
                            onChange={handleInputChange}
                            className="pDescripton"
                        />

                        <div className="pCategory">
                            <p>{t('product_category')}</p>
                            <ApiSelect
                                className="cat"
                                token={token}
                                group={group}
                                onSelect={handleCategorySelect}
                            />
                        </div>

                        <NewInput
                            id="priority"
                            label={t('priority')}
                            name="priority"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9۰-۹]*"
                            placeholder={t('enter_product_priority')}
                            value={form.priority}
                            onChange={handleNumericChange}
                            onKeyDown={handleNumericKeyDown}
                        />

                        <div className="pImage">
                            <div className="image">
                                <img src={preview} alt="Preview" />
                            </div>
                            <input
                                id="image"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.inStock}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            inStock: e.target.checked,
                                        }))
                                    }
                                    color="primary"
                                    className="iosSwitch"
                                />
                            }
                            label={t('product_stock')}
                            labelPlacement="start"
                            className="inStockSwitch"
                        />
                        <button className="submitBtn" type="submit">
                            {t('add_product')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ProductForm;
